import { createBigDecimal, plus } from '@0xtorch/big-decimal'
import type { Transfer } from '@0xtorch/core'
import type { CostBasis } from '../../types/costBasis'
import type { Transaction } from '../../types/transaction'
import { createAssetKey } from '../../utils'
import type {
  CreateCategoryTransactionParameters,
  CreateCategoryTransactionReturnTypes,
} from './types'
import { addBalanceToAssetBorrowsAndAssetBalances } from './util/addBalanceToAssetBorrowsAndAssetBalances'

type CreateInsufficientTransactionParameters =
  CreateCategoryTransactionParameters & {
    readonly transfer: Transfer
    readonly costBasis: CostBasis
  }

type CreateInsufficientTransactionReturnTypes =
  CreateCategoryTransactionReturnTypes

export const createInsufficientTransaction = ({
  actionId,
  timestamp,
  transfer,
  assetBalances,
  assetBorrows,
  usedBalances,
  costBasis,
}: CreateInsufficientTransactionParameters): CreateInsufficientTransactionReturnTypes => {
  const key = createAssetKey(transfer.asset)
  const price = createBigDecimal(0n)
  const value = createBigDecimal(0n)

  // Add balance
  const { assetBalances: keyAssetBalances, assetBorrows: keyAssetBorrows } =
    transfer.asset.type === 'FiatCurrency'
      ? {
          assetBalances: [],
          assetBorrows: [],
        }
      : addBalanceToAssetBorrowsAndAssetBalances({
          amount: transfer.amount,
          value,
          assetBalances: assetBalances.get(key) ?? [],
          assetBorrows: assetBorrows.get(key) ?? [],
          costBasis,
        })
  if (keyAssetBalances.length > 0) {
    assetBalances.set(key, keyAssetBalances)
  } else {
    assetBalances.delete(key)
  }
  if (keyAssetBorrows.length > 0) {
    assetBorrows.set(key, keyAssetBorrows)
  } else {
    assetBorrows.delete(key)
  }

  const balance = (assetBalances.get(key) ?? []).reduce(
    (acc, assetBalance) => plus(acc, assetBalance.amount),
    createBigDecimal(0n),
  )
  const borrowing = (assetBorrows.get(key) ?? []).reduce(
    (acc, assetBorrow) => plus(acc, assetBorrow.amount),
    createBigDecimal(0n),
  )

  const transaction: Transaction = {
    actionId,
    order: 0,
    timestamp,
    category: 'insufficient',
    ins: [
      {
        amount: transfer.amount,
        asset: transfer.asset,
        price,
        zeroCompleted: false,
        cost: value,
        balance,
        borrowing,
        feePl: createBigDecimal(0n),
        incomePl: createBigDecimal(0n),
        tradePl: createBigDecimal(0n),
        totalPl: createBigDecimal(0n),
      },
    ],
    outs: [],
  }

  return {
    transaction,
    assetBalances,
    assetBorrows,
    usedBalances,
  }
}
